import { memo, useState, useCallback } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { DanceRoles } from '../../../pure-js/types/GrooverTypes'
import { Activity, Client } from '../../../pure-js/types/GrooverTypes'
import { CourseRegistrationFormValues } from '../helpers/CourseRegistrationStepOneHelper'
import { State } from '../hooks/useAppState'
import { StorageKey, ReturningUserProfile, getStoredReturningUserProfile } from '../hooks/useLocalStorageItemHelper'
import CourseRegistrationStepOneNoPartnerForm from './CourseRegistrationStepOneNoPartnerForm'
import CourseRegistrationStepOnePartnerForm from './CourseRegistrationStepOnePartnerForm'
import Box from './Box'
import GrooverText from './GrooverText'
import ReturningUserBanner from './ReturningUserBanner'

import styled from '@emotion/styled'
import { DiscountPricingConfig } from '../../../pure-js/types/DiscountTypes'

type Props = {
  state: State
  client: Client
  activity: Activity
  formProps: UseFormReturn<CourseRegistrationFormValues, any, CourseRegistrationFormValues>
  role: DanceRoles | null
  hasPartner: boolean
  onNextStep: () => void
  setHasPartner: (hasPartner: boolean) => void
  changeRole: (isPartnerRole: boolean, newRole: DanceRoles) => void
  partnerRole: DanceRoles | null
  onSubmit: (data: CourseRegistrationFormValues) => void
  onSetIsMainUserStudent: (isMainUserStudent: boolean) => void
  onSetIsMainUserSibling: (isMainUserSibling: boolean) => void
  onSetIsMainUserSenior: (isMainUserSenior: boolean) => void
  onSetIsPartnerStudent: (isPartnerStudent: boolean) => void
  onSetIsPartnerSibling: (isPartnerSibling: boolean) => void
  onSetIsPartnerSenior: (isPartnerSenior: boolean) => void
  onSetDiscountType: (discountType: DiscountPricingConfig['registrationType']) => void
  onSetDiscountRegion: (region: string) => void
  handleRemovePartner: (
    formProps: UseFormReturn<CourseRegistrationFormValues, any, CourseRegistrationFormValues>
  ) => void
  disableRemovePartner?: boolean
  onMainUserPhoneCountryChange?: (country: string) => void
  onPartnerPhoneCountryChange?: (country: string) => void
  isCheckingUnfinishedEnrollment?: boolean
}

const InfoBox = styled(Box)`
  background-color: #f3f8ff;
  border: 1px solid #cce1ff;
  border-radius: 12px;
  padding: 24px 16px;
`

const getReturningUserProfile = getStoredReturningUserProfile

const CourseRegistrationStepOneOpenedContent = ({
  state,
  client,
  activity,
  formProps,
  role,
  hasPartner,
  onNextStep,
  setHasPartner,
  changeRole,
  partnerRole,
  onSubmit,
  onSetIsMainUserStudent,
  onSetIsMainUserSibling,
  onSetIsMainUserSenior,
  onSetIsPartnerStudent,
  onSetIsPartnerSibling,
  onSetIsPartnerSenior,
  onSetDiscountType,
  onSetDiscountRegion,
  handleRemovePartner,
  disableRemovePartner,
  onMainUserPhoneCountryChange,
  onPartnerPhoneCountryChange,
  isCheckingUnfinishedEnrollment = false
}: Props) => {
  const [bannerDismissed, setBannerDismissed] = useState(false)
  const [returningProfile] = useState<ReturningUserProfile | null>(() => {
    if (state.isRegistrationSubmitted || state.isRegistrationCompleted) return null
    if (state.obfuscatedEnrollment?.mainUser?.phone) return null
    return getReturningUserProfile()
  })

  const showBanner = !bannerDismissed && !!returningProfile

  const handleApplyProfile = useCallback(() => {
    if (!returningProfile) return
    formProps.setValue('name', returningProfile.name, { shouldDirty: true, shouldValidate: true })
    formProps.setValue('email', returningProfile.email, { shouldDirty: true, shouldValidate: true })
    formProps.setValue('phoneNumber', returningProfile.phone, { shouldDirty: true, shouldValidate: true })
    if (returningProfile.role) {
      changeRole(false, returningProfile.role as DanceRoles)
    }
  }, [returningProfile, formProps, changeRole])

  const handleDismissBanner = useCallback(() => {
    setBannerDismissed(true)
  }, [])

  return (
    <>
      {showBanner && (
        <ReturningUserBanner
          returningProfile={returningProfile}
          onApply={handleApplyProfile}
          onDismiss={handleDismissBanner}
        />
      )}
      <CourseRegistrationStepOneNoPartnerForm
        state={state}
        client={client}
        activity={activity}
        formProps={formProps}
        role={role}
        onNextStep={onNextStep}
        changeRole={(isPartnerRole, newRole) => changeRole(isPartnerRole, newRole)}
        hasPartner={hasPartner}
        setHasPartner={setHasPartner}
        onSubmit={onSubmit}
        onSetIsMainUserStudent={onSetIsMainUserStudent}
        onSetIsMainUserSibling={onSetIsMainUserSibling}
        onSetIsMainUserSenior={onSetIsMainUserSenior}
        onSetDiscountType={onSetDiscountType}
        onSetDiscountRegion={onSetDiscountRegion}
        isSingleRegistration={activity.singleRegistration}
        onMainUserPhoneCountryChange={onMainUserPhoneCountryChange}
        isCheckingUnfinishedEnrollment={isCheckingUnfinishedEnrollment}
      />
      {/* Show couple info only if hasPartner is true */}
      {hasPartner && (
        <InfoBox fullWidth>
          <GrooverText textKey="registrationStep1CoupleInfo" />
        </InfoBox>
      )}
      {(hasPartner || state.obfuscatedEnrollment.partnerUser?.phone) && !activity.singleRegistration ? (
        <CourseRegistrationStepOnePartnerForm
          state={state}
          client={client}
          activity={activity}
          formProps={formProps}
          partnerRole={partnerRole}
          onNextStep={onNextStep}
          changeRole={(isPartnerRole, newRole) => changeRole(isPartnerRole, newRole)}
          handleRemovePartner={() => handleRemovePartner(formProps)}
          disableRemovePartner={disableRemovePartner}
          onSubmit={onSubmit}
          onSetIsPartnerStudent={onSetIsPartnerStudent}
          onSetIsPartnerSibling={onSetIsPartnerSibling}
          onSetIsPartnerSenior={onSetIsPartnerSenior}
          onMainUserPhoneCountryChange={onMainUserPhoneCountryChange}
          onPartnerPhoneCountryChange={onPartnerPhoneCountryChange}
          isCheckingUnfinishedEnrollment={isCheckingUnfinishedEnrollment}
        />
      ) : null}
    </>
  )
}

export default memo(CourseRegistrationStepOneOpenedContent)
